// t() reads the page language, which the compiler cannot see.
"use no memo";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState, type FormEvent } from "react";
import ArrowLeft from "~icons/ph/arrow-left";
import LinkBreak from "~icons/ph/link-break";
import UsersThree from "~icons/ph/users-three";
import Video from "~icons/ph/video-camera";
import {
  groupCallFull,
  groupCallRejected,
  maxGroupCallPeople,
} from "../../shared/api";
import { useAppLocation } from "../app/location";
import { useShell } from "../app/shell";
import { SiteHeader } from "../app/site-header";
import { ApiError } from "../data/api";
import {
  useJoinGroupCall,
  useStartGroupCall,
  useSubmitting,
} from "../data/mutations";
import { groupInviteOptions } from "../data/queries";
import { useAccount, useGuest, useProtection } from "../data/session";
import { number, t } from "../i18n";
import { CallHeading } from "../links/call-heading";
import { CallInviteLoading, VisitorFields } from "../links/call-link";
import { Avatar } from "../ui/components";
import { notify } from "../ui/toast";

const endedStatus = (error: unknown) =>
  error instanceof ApiError && [400, 404].includes(error.status);

function Ended() {
  return (
    <div className="call-invite">
      <span className="call-invite-icon" aria-hidden="true">
        <LinkBreak width={28} height={28} />
      </span>
      <h1 id="invite-title">{t("انتهت هذه المكالمة الجماعية.")}</h1>
      <a className="button secondary" href="/">
        {t("الذهاب إلى رنّة")}
        <ArrowLeft
          className="directional"
          width={16}
          height={16}
          aria-hidden="true"
        />
      </a>
    </div>
  );
}

const refreshMs = 10000;

function Invite({ code }: { code: string }) {
  const shell = useShell();
  const me = useAccount()?.me ?? null,
    guest = useGuest();
  // The count, a full group call and an ended one all change while this is open.
  const invite = useQuery({
    ...groupInviteOptions(code, me?.id ?? null),
    refetchInterval: ({ state }) =>
      endedStatus(state.error) ? false : refreshMs,
  });
  const { protection, failed, retry } = useProtection();
  const join = useJoinGroupCall(),
    submitting = useSubmitting();
  const [waiting, setWaiting] = useState<{ camera: boolean } | null>(null);
  const autoAttempted = useRef(false),
    mounted = useRef(true),
    currentUser = useRef(me?.id);
  useEffect(() => {
    if (currentUser.current && currentUser.current !== me?.id) {
      setWaiting(null);
      autoAttempted.current = false;
    }
    currentUser.current = me?.id;
  }, [me?.id]);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const [name, setName] = useState(""),
    [token, setToken] = useState(""),
    [attempt, setAttempt] = useState(0),
    [error, setError] = useState("");
  const { refetch } = invite,
    inCall = !!shell.groupCall.live;
  // Whoever just left finds out at once what they left.
  useEffect(() => {
    if (!inCall) void refetch();
  }, [inCall, refetch]);
  // Private invitations stay out of search results.
  useEffect(() => {
    const robots = document.createElement("meta");
    robots.name = "robots";
    robots.content = "noindex";
    document.head.appendChild(robots);
    return () => {
      robots.remove();
    };
  }, []);
  async function enter(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    const { submitter } = event.nativeEvent as SubmitEvent;
    try {
      // Without an account the visitor becomes one under this name.
      const entry = await join.mutateAsync({
        link: code,
        ...(me ? {} : { name, turnstileToken: token || undefined }),
      });
      join.reset();
      const camera =
        submitter instanceof HTMLElement && submitter.dataset.media === "video";
      if (entry.kind === "waiting") {
        setWaiting({ camera });
        void refetch();
      } else await shell.groupCall.enter(entry, camera);
    } catch (e) {
      // LiveKit refuses a place that just went as a bare socket error, so the
      // count is what can say why, and the page already says it's full.
      const { data } = await refetch();
      if (!data || data.people < maxGroupCallPeople)
        setError((e as Error).message);
    } finally {
      // Verification tokens are single-use.
      setToken("");
      setAttempt((n) => n + 1);
    }
  }
  const request = invite.data?.request;
  const { mutateAsync: requestJoin, reset: resetJoin } = join;
  const { enter: connect } = shell.groupCall;
  useEffect(() => {
    if (request === "waiting" && !waiting) setWaiting({ camera: false });
    if (request !== "approved" || autoAttempted.current || inCall) return;
    autoAttempted.current = true;
    const account = me?.id;
    void (async () => {
      try {
        const entry = await requestJoin({ link: code });
        resetJoin();
        if (!mounted.current || currentUser.current !== account) return;
        if (entry.kind === "joined") {
          setWaiting(null);
          await connect(entry, waiting?.camera ?? false);
        }
      } catch (error) {
        if (!mounted.current || currentUser.current !== account) return;
        setWaiting(null);
        setError(error instanceof Error ? error.message : String(error));
      }
    })();
  }, [request, waiting, inCall, code, me?.id, requestJoin, resetJoin, connect]);
  if (endedStatus(invite.error)) return <Ended />;
  if (invite.isError && !invite.data)
    return (
      <div className="call-invite">
        <h1 id="invite-title">{t("تعذر تحميل الدعوة.")}</h1>
        <button
          className="button secondary"
          onClick={() => void invite.refetch()}
        >
          {t("إعادة المحاولة")}
        </button>
      </div>
    );
  if (!invite.data) return <CallInviteLoading paused={shell.covered} />;
  const { organizer, people } = invite.data,
    full = people >= maxGroupCallPeople;
  return (
    <div className="call-invite">
      <Avatar person={organizer} large />
      <p className="eyebrow">{t("دعوة إلى مكالمة جماعية")}</p>
      {organizer.id === me?.id ? (
        <h1 id="invite-title">{t("هذه مكالمتك الجماعية")}</h1>
      ) : (
        <CallHeading name={organizer.name}>
          {"{name} يدعوك إلى مكالمة جماعية"}
        </CallHeading>
      )}
      <p className="call-invite-lead" role="status">
        {full
          ? t(groupCallFull)
          : people
            ? t("في المكالمة الآن: {count}", { count: number(people) })
            : t("لا أحد في المكالمة الآن.")}
      </p>
      {request === "rejected" ? (
        <p className="form-error" role="alert">
          {t(groupCallRejected)}
        </p>
      ) : waiting || request === "waiting" ? (
        <div className="call-invite-card" role="status">
          <p>{t("في انتظار موافقة المنظّم…")}</p>
          <p className="join-note">
            {t(
              "لم تنضم بعد. سيبدأ اتصالك تلقائيًا عند الموافقة، حتى لو كان المنظّم غائبًا الآن.",
            )}
          </p>
          {error && (
            <p className="form-error" role="alert">
              {t(error)}
            </p>
          )}
        </div>
      ) : guest ? (
        <div className="call-invite-card">
          <p className="call-invite-lead">
            {t("أنشئ حسابًا للانضمام إلى هذه المكالمة الجماعية.")}
          </p>
          <div className="call-invite-actions">
            <button
              className="button"
              onClick={() => shell.open({ kind: "keep" })}
            >
              {t("ابقَ على تواصل")}
            </button>
          </div>
        </div>
      ) : (
        <form className="call-invite-card" onSubmit={(e) => void enter(e)}>
          {me ? (
            <p className="call-invite-self">
              <Avatar person={me} />
              {t("ستنضم باسم {name}.", { name: me.name })}
            </p>
          ) : (
            <VisitorFields
              name={name}
              onName={setName}
              attempt={attempt}
              onToken={setToken}
              protection={protection}
              protectionFailed={failed}
              onRetry={retry}
            />
          )}
          {error && (
            <p className="form-error" role="alert">
              {t(error)}
            </p>
          )}
          <div className="call-invite-actions">
            {(["audio", "video"] as const).map((media) => (
              <button
                key={media}
                className={`button ${media === "video" ? "secondary" : ""}`}
                data-media={media}
                aria-busy={submitting}
                disabled={
                  submitting ||
                  full ||
                  (!me && (!protection || (protection.required && !token)))
                }
              >
                {media === "video" ? (
                  <Video width={18} height={18} aria-hidden="true" />
                ) : (
                  <UsersThree width={18} height={18} aria-hidden="true" />
                )}
                {t(
                  media === "video"
                    ? "انضم بالفيديو"
                    : "انضم إلى المكالمة الجماعية",
                )}
              </button>
            ))}
          </div>
        </form>
      )}
      {!me && !guest && !waiting && (
        <>
          <p className="join-note">
            {t(
              "ستحصل على حساب في رنّة بهذا الاسم، دون بريد إلكتروني أو كلمة مرور.",
            )}
          </p>
          <button
            className="restore-link"
            onClick={() => shell.open({ kind: "enter", step: "restore" })}
          >
            {t("لديك حساب؟ سجّل الدخول")}
          </button>
        </>
      )}
    </div>
  );
}

// A group link's page: who invited you, and joining.
export function GroupLinkScreen() {
  const { groupCode } = useAppLocation();
  const me = useAccount()?.me;
  return (
    <>
      {!me && <SiteHeader />}
      <main id="main-content" tabIndex={-1} className="search-home">
        <section
          className="search-hero invite-hero"
          aria-labelledby="invite-title"
        >
          {/* Another link starts over. */}
          <Invite key={groupCode} code={groupCode ?? ""} />
        </section>
      </main>
    </>
  );
}

// Starting joins too, and starting again returns to the one that's live.
export function StartGroupCall() {
  const { groupCall, open } = useShell();
  const start = useStartGroupCall(),
    submitting = useSubmitting();
  async function enter() {
    try {
      const entry = await start.mutateAsync();
      // The token is the SDK's from here on.
      start.reset();
      await groupCall.enter(entry);
    } catch (e) {
      notify((e as Error).message);
    }
  }
  return (
    <div className="group-start-actions">
      <button
        className="button secondary call-link-create"
        disabled={submitting}
        aria-busy={submitting}
        onClick={() => void enter()}
      >
        <UsersThree width={18} height={18} aria-hidden="true" />
        {t("بدء مكالمة جماعية")}
      </button>
      <button
        className="quiet-button"
        disabled={submitting}
        onClick={() => open({ kind: "group-friends" })}
      >
        {t("اختيار أصدقاء ودعوتهم")}
      </button>
    </div>
  );
}
